import { DateTime } from 'luxon';
import numeral from 'numeral';
import { Constants } from '@pwc/pwc-deals-web-common';
import _isFinite from 'lodash/isFinite';
import {
  defaultFinancialsDecimalCount,
  defaultCurrency,
  defaultFinancialsMagnitude,
} from '../../features/project/projectSlice';
import {
  EBITDA,
  EMPTY_DATA_MARKER,
  NQ_EBITDA_CONTRIBUTION,
  WORKING_CAPITAL,
} from '../engine/utilities/constants';

export const isEmptyText = (text = '') => text.trim().length === 0;

export const itemsCountText = (items = [], itemsName = 'Items') => {
  const countText = items.length
    ? ` (${items.length.toString().padStart(2, '0')})`
    : '';

  return `${itemsName}${countText}`;
};

export const displayDate = (dateToFormat = new Date()) => {
  const dt = DateTime.fromJSDate(dateToFormat);
  return dt.toFormat('dd LLL yyyy');
};

const financialsFormatDefaultOptions = {
  decimalCount: defaultFinancialsDecimalCount,
  formatForScreenReader: false,
  emptyDataMarker: EMPTY_DATA_MARKER,
};

export const financialsFormat = (options = financialsFormatDefaultOptions) => {
  const decimalFormatSection =
    options.decimalCount === 0 ? '' : `.${'0'.repeat(options.decimalCount)}`;

  const negativeValuePrefix = options.formatForScreenReader ? '' : '(';
  const negativeValueSuffix = options.formatForScreenReader ? '' : ')';

  return `${negativeValuePrefix}0,0${decimalFormatSection}${negativeValueSuffix}`;
};

const hasScientificNotationRegEx = /e/g;

export const financialValue = (value = 0, options = {}) => {
  let cleanValue = value;
  if (Number.isNaN(value)) {
    cleanValue = '';
  }

  if (typeof value === 'symbol') {
    cleanValue = 0;
  }

  const effectiveOptions = { ...financialsFormatDefaultOptions, ...options };

  let floatValue = parseFloat(cleanValue);

  const isNumericValue = _isFinite(floatValue);

  if (isNumericValue && hasScientificNotationRegEx.test(floatValue)) {
    floatValue = parseFloat(floatValue.toFixed(8));
  }

  if (isNumericValue && floatValue === 0) {
    return effectiveOptions.emptyDataMarker;
  }

  return isNumericValue
    ? numeral(floatValue).format(financialsFormat(effectiveOptions))
    : cleanValue;
};

export const valueAsPercent = (value = 0) => {
  const floatValue = parseFloat(value);
  return _isFinite(floatValue) ? numeral(floatValue).format('(0.0%)') : value;
};

const defaultCurrencySymbol = '$';

export const getCurrencySymbol = (currency = defaultCurrency) => {
  const currencyOption = Constants.CurrencyOptions.find(
    (option) => option.value.toLowerCase() === currency.toLowerCase()
  );
  return (currencyOption && currencyOption.symbol) || defaultCurrencySymbol;
};

export const getFinancialScale = (settings) => {
  if (!settings)
    return `${getCurrencySymbol(
      defaultCurrency
    )} in ${defaultFinancialsMagnitude.toLowerCase()}`;

  const currencySymbol = getCurrencySymbol(
    settings.currency || defaultCurrency
  );
  const effectiveMagnitude =
    settings.financialsMagnitude || defaultFinancialsMagnitude;
  const effectiveMagnitudeLower = effectiveMagnitude.toLowerCase();

  return `${currencySymbol} in ${effectiveMagnitudeLower}`;
};

// Matches strings that end with any of the following - case insensitive
// adjustments (+/-NQ)
// adj. (+/-NQ)
// adjustments
// adjustment
// adj.
const endsWithAdjustmentsRegEx =
  /(adjustments \(\+\/-NQ\)|adj\. \(\+\/-NQ\)|adjustments|adj\.|adjustment)$/gi;

// Matches strings that end with (+/-NQ)
const endsWithNqFlagRegEx = /\(\+\/-NQ\)$/i;

const adjustmentTypeContributionName = (text, contributionType) => {
  const hasNqSuffix = endsWithNqFlagRegEx.test(text);

  const derived = text.replace(endsWithAdjustmentsRegEx, 'Adjusted');

  return `${derived} ${contributionType}${
    hasNqSuffix ? ` ${NQ_EBITDA_CONTRIBUTION}` : ''
  }`;
};

export const adjustmentTypeEbitdaContributionName = (text) => {
  return adjustmentTypeContributionName(text, EBITDA);
};

export const adjustmentTypeWorkingCapitalContributionName = (text) => {
  return adjustmentTypeContributionName(text, WORKING_CAPITAL);
};

// Matches strings that start with:
// L
// any number of contiguous non-space characters
// Q or M
// without regard to capitalization
const startsWithLastNPeriodsRegEx = /^[L][\S]*[QM]/i;

export const isLastNPeriodLabel = (text) =>
  startsWithLastNPeriodsRegEx.test(text);

const isReportedDebtRegEx = /^bank debt$|^reported debt$/i;
export const isReportedDebt = (text) => isReportedDebtRegEx.test(text);

const isReportedCashRegEx = /^less cash$|^reported cash$/i;
export const isReportedCash = (text) => isReportedCashRegEx.test(text);

const isDebtLikeItemsRegEx = /^debt-like items?$/i;
export const isDebtLikeItems = (text) => isDebtLikeItemsRegEx.test(text);

const isRevenueRegEx = /^revenues?$|^revenues \(p&l\)$/i;
export const isRevenue = (text) => isRevenueRegEx.test(text);

export const removeBreakspaces = (content) =>
  content.replace(/(<p class="rich-text-editor-intro">&nbsp;<\/p>)/g, '');
